import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline';
import { Outlet, useLocation, useMatch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { DebugMenu } from '@/features/debug-menu/ui/DebugMenu';
import { StickyNoteOverlay } from '@/features/debug-menu/ui/StickyNoteOverlay';
import {
  MAX_LEFT_SIDE_NAVIGATION_WIDTH_OPEN,
  SIDE_NAVIGATION_WIDTH_CLOSED,
  SIDE_NAVIGATION_WIDTH_OPEN,
  SIDE_NAVIGATION_Z_INDEX,
} from '@/shared/lib/constants';
import { VITE_ENV_NAME } from '@/shared/lib/env';
import { isDebugEnabled } from '@/shared/lib/system';
import { useSideNavigationStore } from '@/shared/stores/side-navigation-store';
import { type AIContext, useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import {
  BaseLayout,
  Button,
  LayoutOffsetProvider,
  PAGE_LAYOUT_FRAME_SCROLL_CSS_VAR,
  ProductLogo,
  Separator,
  SidePanel,
  SidePanelLeaveAlert,
} from '@/shared/ui';
import { NavMenuItems } from '@/shared/ui/layout/nav-menu-items/NavMenuItems';
import { RightSideMenuContents } from '@/widgets/right-side-menu/ui/RightSideMenuContents';
import { useProjectDetailSidePanelEffect } from '@/widgets/route-layout/lib/useProjectDetailSidePanelEffect';
import { EnvironmentIndicator } from './EnvironmentIndicator';
import { useRouteMenu } from './RouteMenu';
import { SupportManualButton } from './SupportManualButton';
import { UserMenu } from './UserMenu';

const getHeaderEnvironmentClassName = (envName: string) => {
  // ローカル開発（vite dev mode）は LOCAL の配色
  if (import.meta.env.DEV) {
    return 'border-stone-300 bg-stone-100';
  }

  const normalized = envName.toLowerCase();

  if (normalized === 'dev') {
    return 'border-sky-200 bg-sky-50';
  }

  if (normalized === 'stg') {
    return 'border-amber-200 bg-amber-50';
  }

  if (normalized === 'preprod') {
    return 'border-violet-200 bg-violet-50';
  }

  // VITE_ENV_NAME 未設定や想定外の値は PROD として扱う（client は元仕様で PROD 配色なし）
  return null;
};

export const MainRouteLayout = () => {
  const sidebarMode = useSideNavigationStore((state) => state.mode);
  const toggleVisibility = useSideNavigationStore((state) => state.toggleVisibility);
  const toggleCollapse = useSideNavigationStore((state) => state.toggleCollapse);
  const isSidebarOpen = sidebarMode === 'open';
  const isSidebarVisible = sidebarMode !== 'hidden';
  const isRightOpen = useRouteSidePanelStore((state) => state.isOpen);
  const setOpen = useRouteSidePanelStore((state) => state.setOpen);
  const setHasUnsavedChanges = useRouteSidePanelStore((state) => state.setHasUnsavedChanges);
  const items = useRouteMenu();

  // URL の threadId からチャットパネルを復元する。
  // 事業詳細ページとメモページの両方で AI チャットを共有するため、どちらのマッチでも復元する。
  //
  // パネルが閉じている間は `RightSideMenuContents` がアンマウントされているため、
  // `useRouteSidebarEffect` 経由では URL 変更を拾えない。
  // メモ詳細のスレッドリンクなど「閉じた状態で threadId が URL に追加される」ケースをここで拾う。
  // パネルが既に開いている時は `useRouteSidebarEffect` が threadId 更新を担うため、何もしない。
  const projectDetailMatch = useMatch({
    from: '/_protected/projects/$projectId/',
    shouldThrow: false,
  });
  const projectMemoMatch = useMatch({
    from: '/_protected/projects/$projectId/memo',
    shouldThrow: false,
  });
  const urlThreadId = projectDetailMatch?.search.threadId ?? projectMemoMatch?.search.threadId;
  useEffect(() => {
    if (!urlThreadId) return;
    const panel = useRouteSidePanelStore.getState();
    if (panel.isOpen) return;
    if (
      panel.mode?.type === 'ai-chat' &&
      panel.mode.context?.route === 'projectDetail' &&
      panel.mode.context.mode === 'agent_chat' &&
      panel.mode.context.threadId === urlThreadId
    ) {
      return;
    }
    const context: AIContext = {
      route: 'projectDetail',
      mode: 'agent_chat',
      threadId: urlThreadId,
    };
    setOpen({ isOpen: true, mode: { type: 'ai-chat', context } });
  }, [urlThreadId, setOpen]);

  // 事業詳細ページ・メモページのサイドパネル開閉ライフサイクル（ITA-2626）
  // - 到達時にデフォルトで AI チャットを開く
  // - リロード時は閉じた状態を保持する
  // - 詳細は useProjectDetailSidePanelEffect の docstring 参照
  const projectDetailProjectId =
    projectDetailMatch?.params.projectId ?? projectMemoMatch?.params.projectId;
  useProjectDetailSidePanelEffect({ projectDetailProjectId, urlThreadId });

  const [rightPanelWidth, setRightPanelWidth] = useState(0);
  const mainScrollRef = useRef<HTMLDivElement | null>(null);

  // SecondaryPanel を持つレイアウト（master / assessment / AIマッチング結果）では、
  // 二次メニュー幅 + 右パネル幅で main の可用幅が狭くなる。
  // BaseLayout 既定の minWidth: MIN_MAIN_CONTENT_WIDTH(720) を保つと main 自体が
  // 横スクロールし、二次メニュー / breadcrumb / 本文の Banner が一緒に流れる。
  // これらのページではテーブル等が自前で overflow-x スクロールを提供できるため、
  // main の minWidth を 0 に上書きして main 自体の横スクロールを抑止する。
  const pathname = useLocation({ select: (location) => location.pathname });
  const hasSecondaryPanel =
    pathname.startsWith('/projects/ai-matching-results') ||
    pathname.startsWith('/master') ||
    pathname.startsWith('/assessment/');

  useEffect(() => {
    const mainElement = mainScrollRef.current;
    if (!mainElement) {
      return undefined;
    }

    const root = document.documentElement;
    // fixedヘッダーを横スクロールするメインエリアと同期させるためにCSS変数を更新する。
    const updateScrollVariable = () => {
      root.style.setProperty(PAGE_LAYOUT_FRAME_SCROLL_CSS_VAR, `${-mainElement.scrollLeft}px`);
    };

    updateScrollVariable();

    const handleScroll = () => {
      updateScrollVariable();
    };

    mainElement.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      mainElement.removeEventListener('scroll', handleScroll);
      root.style.removeProperty(PAGE_LAYOUT_FRAME_SCROLL_CSS_VAR);
    };
  }, []);

  // 右サイドバーが閉じたタイミングでサイドパネルの未保存状態をリセット
  // フォームのダーティ状態（formDirtyStore）はForm.tsxが管理するため、ここではリセットしない
  useEffect(() => {
    if (!isRightOpen) {
      setHasUnsavedChanges(false);
    }
  }, [isRightOpen, setHasUnsavedChanges]);

  return (
    <BaseLayout
      headerClassName={getHeaderEnvironmentClassName(VITE_ENV_NAME) ?? undefined}
      headerLeftSection={<EnvironmentIndicator />}
      headerRightSection={
        <div className="flex items-center gap-2">
          <SupportManualButton />
          <UserMenu />
        </div>
      }
      onToggleSidebar={toggleVisibility}
      sidebar={
        <SidePanel
          isOpen={isSidebarOpen}
          side="left"
          position="sticky"
          defaultWidth={SIDE_NAVIGATION_WIDTH_OPEN}
          collapsedWidth={isSidebarVisible ? SIDE_NAVIGATION_WIDTH_CLOSED : 0}
          enableResize={false}
          keepChildrenMounted
          enableContentFade={false}
          zIndex={SIDE_NAVIGATION_Z_INDEX}
          className="border-stone-200 border-r bg-surface-strength1 shadow-none transition-all duration-200"
          contentClassName="flex h-full flex-col p-2"
        >
          {/* サイドメニューヘッダー */}
          {isSidebarOpen ? (
            <div className="flex items-center justify-between px-3 py-1">
              <ProductLogo />
              <Button variant="ghost" size="icon" onClick={toggleCollapse}>
                <ChevronDoubleLeftIcon />
              </Button>
            </div>
          ) : (
            <div className="flex justify-center py-1">
              <Button variant="ghost" size="icon" onClick={toggleCollapse}>
                <ChevronDoubleRightIcon />
              </Button>
            </div>
          )}
          <Separator className="my-1" />
          {/* メニューアイテム */}
          <NavMenuItems items={items} isOpen={isSidebarOpen} />
        </SidePanel>
      }
      rightPanel={
        <SidePanel
          isOpen={isRightOpen}
          side="right"
          position="sticky"
          enableResize
          defaultWidth={640}
          minWidth={320}
          maxWidth={MAX_LEFT_SIDE_NAVIGATION_WIDTH_OPEN}
          onWidthChange={setRightPanelWidth}
        >
          <RightSideMenuContents />
        </SidePanel>
      }
      mainRef={mainScrollRef}
      mainMinWidth={hasSecondaryPanel ? 0 : undefined}
      extra={
        <>
          {/* SidePanel(右)離脱確認ダイアログ */}
          {isRightOpen && <SidePanelLeaveAlert />}
          {/* デバッグメニュー・付箋（dev 環境 or ローカルのみ表示） */}
          {isDebugEnabled() && (
            <>
              <DebugMenu />
              <StickyNoteOverlay />
            </>
          )}
        </>
      }
    >
      <LayoutOffsetProvider rightOffset={rightPanelWidth}>
        <Outlet />
      </LayoutOffsetProvider>
    </BaseLayout>
  );
};
